import {
  type DropTargetGetFeedback,
  IconButton,
  useDraggable,
  useDropTarget,
} from '@affine/component';
import { NavigationPanelService } from '@affine/core/modules/navigation-panel';
import type { AffineDNDData } from '@affine/core/types/dnd';
import {
  ArrowsLeftIcon,
  ArrowsRightIcon,
  CloseIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import {
  type ComponentProps,
  type DragEvent as ReactDragEvent,
  forwardRef,
  type ReactElement,
  type ReactNode,
  type RefObject,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';

import { CollapsibleSection } from '../../desktop/components/navigation-panel';
import * as navigationTreeStyles from '../../desktop/components/navigation-panel/tree/node.css';
import { isSidebarAreaHeaderDropTarget } from '../../desktop/components/navigation-panel/tree/sidebar-area-drag-hover';
import { useSidebarAreaHeaderDropTarget } from '../root-app-sidebar/use-sidebar-area-header-drop-target';
import {
  SidebarExpandableGroup,
  SidebarExpandableSection,
} from '../sidebar-expandable-section';
import * as styles from './index.css';
import { useWorkspaceAppSidebarAreaResize } from './use-area-resize';

export type WorkspaceAppSidebarElementProps = Omit<
  ComponentProps<'div'>,
  'children' | 'className'
> & {
  [attribute: `data-${string}`]: string | number | boolean | undefined;
};

export type WorkspaceAppSidebarHeaderControl =
  | { type: 'external' }
  | {
      label: string;
      onClick: () => void;
      type: 'collapse';
    }
  | {
      label: string;
      onClick: () => void;
      type: 'close';
      visibility: 'mobile-overlay' | 'two-pane-overlay';
    };

export function WorkspaceAppSidebarToggleButton({
  className,
  direction,
  label,
  onClick,
  open,
  testId,
}: {
  className?: string;
  direction: 'collapse' | 'expand';
  label: string;
  onClick: () => void;
  open?: boolean;
  testId?: string;
}): ReactElement {
  const Icon = direction === 'collapse' ? ArrowsLeftIcon : ArrowsRightIcon;

  return (
    <button
      aria-label={label}
      className={clsx(styles.sidebarToggleButton, className)}
      data-open={open}
      data-testid={testId}
      onClick={onClick}
      type="button"
    >
      <Icon aria-hidden />
    </button>
  );
}

export function WorkspaceAppSidebarFrame({
  as: Root = 'aside',
  children,
  className,
  contentClassName,
  contentProps,
  footer,
  header,
  rootProps,
  status,
}: {
  as?: 'aside' | 'div';
  children?: ReactNode;
  className?: string;
  contentClassName?: string;
  contentProps?: WorkspaceAppSidebarElementProps;
  footer?: ReactNode;
  header: ReactNode;
  rootProps?: WorkspaceAppSidebarElementProps;
  status?: ReactNode;
}): ReactElement {
  return (
    <Root {...rootProps} className={clsx(styles.surface, className)}>
      {header}
      {status}
      <div {...contentProps} className={clsx(styles.body, contentClassName)}>
        {children}
      </div>
      {footer}
    </Root>
  );
}

export function WorkspaceAppSidebarHeader({
  className,
  control = { type: 'external' },
  title,
}: {
  className?: string;
  control?: WorkspaceAppSidebarHeaderControl;
  title: ReactNode;
}): ReactElement {
  return (
    <div className={clsx(styles.header, className)}>
      <span className={styles.title}>{title}</span>
      {control.type === 'collapse' ? (
        <WorkspaceAppSidebarToggleButton
          direction="collapse"
          label={control.label}
          onClick={control.onClick}
        />
      ) : null}
      {control.type === 'close' ? (
        <IconButton
          aria-label={control.label}
          className={clsx(
            styles.closeButton,
            control.visibility === 'two-pane-overlay'
              ? styles.closeButtonTwoPaneOverlay
              : styles.closeButtonMobileOverlay
          )}
          icon={<CloseIcon />}
          onClick={control.onClick}
          size="20"
        />
      ) : null}
    </div>
  );
}

export interface WorkspaceAppScopedSidebarItem {
  scope: 'private' | 'shared';
}

export interface WorkspaceAppSidebarAreaNode {
  key: string;
  scope: 'private' | 'shared';
  title: ReactNode;
}

export interface WorkspaceAppSidebarSourceArea {
  kind: 'app' | 'private' | 'public' | 'teamspace';
  name: string;
  rootFolderId: string;
}

interface WorkspaceAppSidebarDndData extends AffineDNDData {
  draggable: AffineDNDData['draggable'] & {
    workspaceAppSidebarAreaItemKey: string;
  };
}

const noopWorkspaceAppSidebarHeaderDrop = () => {};

function WorkspaceAppSidebarDraggableItem<TItem>({
  item,
  itemKey,
  movable,
  onDragEnd,
  onDragStart,
  renderItem,
}: {
  item: TItem;
  itemKey: string;
  movable: boolean;
  onDragEnd?: () => void;
  onDragStart: (item: TItem) => void;
  renderItem: (
    item: TItem,
    dragHandleRef?: RefObject<HTMLDivElement>
  ) => ReactNode;
}): ReactElement {
  const { dragHandleRef, dragRef, dragging, draggingPosition, dropTarget } =
    useDraggable<WorkspaceAppSidebarDndData>(
      () => ({
        canDrag: movable,
        data: { workspaceAppSidebarAreaItemKey: itemKey },
        disableDragPreview: true,
        onDragStart: () => onDragStart(item),
        onDrop: onDragEnd,
      }),
      [item, itemKey, movable, onDragEnd, onDragStart]
    );
  const areaHeaderHoverActive = dropTarget.some(target =>
    isSidebarAreaHeaderDropTarget(target.data)
  );

  return (
    <>
      <div className={styles.areaItem} ref={dragRef}>
        {renderItem(item, dragHandleRef)}
      </div>
      {dragging && !draggingPosition.outWindow
        ? createPortal(
            <div
              className={navigationTreeStyles.liveDraggingPortal}
              data-area-hover-expand={areaHeaderHoverActive}
              data-testid="workspace-app-sidebar-live-drag-preview"
              style={{
                transform: `translate3d(${draggingPosition.clientX + 8}px, ${draggingPosition.clientY + 4}px, 0)`,
              }}
            >
              <div className={navigationTreeStyles.draggingContainer}>
                <div className={styles.areaDragPreview}>{renderItem(item)}</div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

export function createWorkspaceAppSidebarAreaNodes<
  TArea extends WorkspaceAppSidebarSourceArea,
>(
  areas: readonly TArea[],
  getTitle: (area: TArea) => ReactNode = area => area.name
): Array<WorkspaceAppSidebarAreaNode & { area: TArea }> {
  return areas
    .filter(area => area.kind !== 'app')
    .map(area => ({
      area,
      key: area.rootFolderId,
      scope:
        area.kind === 'private' ? ('private' as const) : ('shared' as const),
      title: getTitle(area),
    }));
}

function WorkspaceAppSidebarNestedArea<
  TArea extends WorkspaceAppSidebarAreaNode,
>({
  area,
  canDropDraggedItem,
  children,
  emptyState,
  itemCount,
  onDropDraggedItem,
  persistenceKey,
  canRevealDraggedItem,
}: {
  area: TArea;
  canDropDraggedItem: (
    source: WorkspaceAppSidebarDndData['draggable']
  ) => boolean;
  children: ReactNode;
  emptyState?: ReactNode;
  itemCount: number;
  onDropDraggedItem: (source: WorkspaceAppSidebarDndData['draggable']) => void;
  persistenceKey: string;
  canRevealDraggedItem: (
    source: WorkspaceAppSidebarDndData['draggable']
  ) => boolean;
}): ReactElement {
  const navigationPanelService = useService(NavigationPanelService);
  const collapsedPath = useMemo(
    () => ['workspaceApp', persistenceKey, 'area', area.key],
    [area.key, persistenceKey]
  );
  const collapsed = useLiveData(
    navigationPanelService.collapsed$(collapsedPath)
  );
  const canDropOnCollapsedHeader = useCallback(
    (args: DropTargetGetFeedback<WorkspaceAppSidebarDndData>) =>
      collapsed && canRevealDraggedItem(args.source.data),
    [canRevealDraggedItem, collapsed]
  );
  const { dropTargetRef: headerDropTargetRef } =
    useSidebarAreaHeaderDropTarget<WorkspaceAppSidebarDndData>({
      canDrop: canDropOnCollapsedHeader,
      collapsed,
      onDrop: noopWorkspaceAppSidebarHeaderDrop,
      path: collapsedPath,
    });
  const { draggedOver, dropTargetRef } =
    useDropTarget<WorkspaceAppSidebarDndData>(
      () => ({
        canDrop: args => canDropDraggedItem(args.source.data),
        dropEffect: 'move',
        onDrop: args => onDropDraggedItem(args.source.data),
      }),
      [canDropDraggedItem, onDropDraggedItem]
    );

  return (
    <div
      className={styles.nestedAreaDropTarget}
      data-drag-over={draggedOver ? true : undefined}
      data-workspace-app-sidebar-area-key={area.key}
      ref={dropTargetRef}
    >
      <CollapsibleSection
        headerRef={headerDropTargetRef}
        path={collapsedPath}
        testId="workspace-app-sidebar-area"
        title={area.title}
      >
        <div className={styles.nestedAreaItems}>
          {itemCount === 0 && emptyState !== undefined ? (
            <div
              className={styles.nestedAreaEmptyState}
              data-sidebar-section-snap-target="true"
            >
              {emptyState}
            </div>
          ) : (
            children
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
}

/**
 * Shared exact-area tree for workspace apps. The tree owns common grouping,
 * persistence, expand/collapse and drag chrome; each app only renders its
 * resource row and handles the requested area transition.
 */
export function WorkspaceAppSidebarAreaTree<
  TItem,
  TArea extends WorkspaceAppSidebarAreaNode,
>({
  areas,
  canDropItem,
  canMoveItem,
  emptyState,
  getItemAreaKey,
  getItemKey,
  items,
  onItemAreaChangeRequest,
  onItemDragStart,
  personalTitle,
  persistenceKey,
  renderItem,
  renderSectionAction,
  teamAreasTitle,
}: {
  areas: readonly TArea[];
  canDropItem?: (item: TItem, targetArea: TArea) => boolean;
  canMoveItem?: (item: TItem) => boolean;
  emptyState?: (scope: WorkspaceAppSidebarAreaNode['scope']) => ReactNode;
  getItemAreaKey: (item: TItem) => string;
  getItemKey: (item: TItem) => string;
  items: readonly TItem[];
  onItemAreaChangeRequest?: (item: TItem, targetArea: TArea) => void;
  onItemDragStart?: (item: TItem) => void;
  personalTitle: ReactNode;
  persistenceKey: string;
  renderItem: (
    item: TItem,
    dragHandleRef?: RefObject<HTMLDivElement>
  ) => ReactNode;
  renderSectionAction?: (
    scope: WorkspaceAppSidebarAreaNode['scope']
  ) => ReactNode;
  teamAreasTitle: ReactNode;
}): ReactElement {
  const navigationPanelService = useService(NavigationPanelService);
  const personalPath = useMemo(
    () => ['workspaceApp', persistenceKey, 'personal'],
    [persistenceKey]
  );
  const teamAreasPath = useMemo(
    () => ['workspaceApp', persistenceKey, 'teamAreas'],
    [persistenceKey]
  );
  const personalCollapsed = useLiveData(
    navigationPanelService.collapsed$(personalPath)
  );
  const teamAreasCollapsed = useLiveData(
    navigationPanelService.collapsed$(teamAreasPath)
  );
  const personalExpanded = !personalCollapsed;
  const teamAreasExpanded = !teamAreasCollapsed;
  const privateAreas = useMemo(
    () => areas.filter(area => area.scope === 'private'),
    [areas]
  );
  const sharedAreas = useMemo(
    () => areas.filter(area => area.scope === 'shared'),
    [areas]
  );
  const itemsByArea = useMemo(() => {
    const grouped = new Map<string, TItem[]>();
    for (const area of areas) grouped.set(area.key, []);
    for (const item of items) {
      const areaKey = getItemAreaKey(item);
      const areaItems = grouped.get(areaKey);
      if (areaItems) areaItems.push(item);
    }
    return grouped;
  }, [areas, getItemAreaKey, items]);
  const itemsByKey = useMemo(
    () => new Map(items.map(item => [getItemKey(item), item] as const)),
    [getItemKey, items]
  );
  const getDraggedItem = useCallback(
    (source: WorkspaceAppSidebarDndData['draggable']) =>
      itemsByKey.get(source.workspaceAppSidebarAreaItemKey),
    [itemsByKey]
  );
  const personalItems = useMemo(
    () => privateAreas.flatMap(area => itemsByArea.get(area.key) ?? []),
    [itemsByArea, privateAreas]
  );
  const sharedItemCount = useMemo(
    () =>
      sharedAreas.reduce(
        (count, area) => count + (itemsByArea.get(area.key)?.length ?? 0),
        0
      ),
    [itemsByArea, sharedAreas]
  );
  const renderDraggableItem = useCallback(
    (item: TItem) => {
      const movable =
        onItemAreaChangeRequest !== undefined && (canMoveItem?.(item) ?? true);
      return (
        <WorkspaceAppSidebarDraggableItem
          item={item}
          itemKey={getItemKey(item)}
          key={getItemKey(item)}
          movable={movable}
          onDragStart={draggedItem => {
            onItemDragStart?.(draggedItem);
          }}
          renderItem={renderItem}
        />
      );
    },
    [
      canMoveItem,
      getItemKey,
      onItemAreaChangeRequest,
      onItemDragStart,
      renderItem,
    ]
  );
  const canDropOnArea = useCallback(
    (targetArea: TArea, source: WorkspaceAppSidebarDndData['draggable']) => {
      const item = getDraggedItem(source);
      return Boolean(
        item &&
        getItemAreaKey(item) !== targetArea.key &&
        canDropItem?.(item, targetArea) !== false
      );
    },
    [canDropItem, getDraggedItem, getItemAreaKey]
  );
  const dropOnArea = useCallback(
    (targetArea: TArea, source: WorkspaceAppSidebarDndData['draggable']) => {
      const item = getDraggedItem(source);
      if (
        !item ||
        getItemAreaKey(item) === targetArea.key ||
        canDropItem?.(item, targetArea) === false
      ) {
        return;
      }
      onItemAreaChangeRequest?.(item, targetArea);
    },
    [canDropItem, getDraggedItem, getItemAreaKey, onItemAreaChangeRequest]
  );
  const privateDropArea = privateAreas[0];
  const {
    draggedOver: personalAreaDraggedOver,
    dropTargetRef: personalDropRef,
  } = useDropTarget<WorkspaceAppSidebarDndData>(
    () => ({
      canDrop: args =>
        Boolean(
          privateDropArea && canDropOnArea(privateDropArea, args.source.data)
        ),
      dropEffect: 'move',
      onDrop: args => {
        if (privateDropArea) dropOnArea(privateDropArea, args.source.data);
      },
    }),
    [canDropOnArea, dropOnArea, privateDropArea]
  );
  const canRevealPersonalHeader = useCallback(
    (args: DropTargetGetFeedback<WorkspaceAppSidebarDndData>) => {
      const item = getDraggedItem(args.source.data);
      return Boolean(
        personalCollapsed &&
        item &&
        privateDropArea &&
        getItemAreaKey(item) !== privateDropArea.key &&
        canDropItem?.(item, privateDropArea) !== false
      );
    },
    [
      canDropItem,
      getDraggedItem,
      getItemAreaKey,
      personalCollapsed,
      privateDropArea,
    ]
  );
  const canRevealTeamAreasHeader = useCallback(
    (args: DropTargetGetFeedback<WorkspaceAppSidebarDndData>) => {
      const item = getDraggedItem(args.source.data);
      return Boolean(
        teamAreasCollapsed &&
        item &&
        sharedAreas.some(
          area =>
            getItemAreaKey(item) !== area.key &&
            canDropItem?.(item, area) !== false
        )
      );
    },
    [
      canDropItem,
      getDraggedItem,
      getItemAreaKey,
      sharedAreas,
      teamAreasCollapsed,
    ]
  );
  const { dropTargetRef: personalHeaderDropTargetRef } =
    useSidebarAreaHeaderDropTarget<WorkspaceAppSidebarDndData>({
      canDrop: canRevealPersonalHeader,
      collapsed: personalCollapsed,
      onDrop: noopWorkspaceAppSidebarHeaderDrop,
      path: personalPath,
    });
  const { dropTargetRef: teamAreasHeaderDropTargetRef } =
    useSidebarAreaHeaderDropTarget<WorkspaceAppSidebarDndData>({
      canDrop: canRevealTeamAreasHeader,
      collapsed: teamAreasCollapsed,
      onDrop: noopWorkspaceAppSidebarHeaderDrop,
      path: teamAreasPath,
    });

  return (
    <WorkspaceAppSidebarAreas
      persistenceKey={persistenceKey}
      personal={{
        emptyState: emptyState?.('private'),
        itemCount: personalItems.length,
        listProps: {
          'data-drag-over': personalAreaDraggedOver ? true : undefined,
        },
        listRef: personalDropRef,
        measurementRef: personalDropRef,
        groupProps: {
          expanded: personalExpanded,
          placement: 'top',
        },
        sectionProps: {
          action: renderSectionAction?.('private'),
          expanded: personalExpanded,
          headerRef: personalHeaderDropTargetRef,
          onExpandedChange: expanded =>
            navigationPanelService.setCollapsed(personalPath, !expanded),
          title: personalTitle,
        },
        children: personalItems.map(renderDraggableItem),
      }}
      teamAreas={{
        content:
          sharedAreas.length === 0 ? (
            <WorkspaceAppSidebarAreaList>
              <div
                className={styles.areaEmptyState}
                data-sidebar-section-snap-target="true"
              >
                {emptyState?.('shared')}
              </div>
            </WorkspaceAppSidebarAreaList>
          ) : (
            <WorkspaceAppSidebarAreaList className={styles.areaTreeList}>
              {sharedAreas.map(area => {
                const areaItems = itemsByArea.get(area.key) ?? [];
                return (
                  <WorkspaceAppSidebarNestedArea
                    area={area}
                    canDropDraggedItem={source => canDropOnArea(area, source)}
                    canRevealDraggedItem={source => {
                      const item = getDraggedItem(source);
                      return Boolean(
                        item &&
                        getItemAreaKey(item) !== area.key &&
                        canDropItem?.(item, area) !== false
                      );
                    }}
                    itemCount={areaItems.length}
                    key={area.key}
                    onDropDraggedItem={source => dropOnArea(area, source)}
                    persistenceKey={persistenceKey}
                  >
                    {areaItems.map(renderDraggableItem)}
                  </WorkspaceAppSidebarNestedArea>
                );
              })}
            </WorkspaceAppSidebarAreaList>
          ),
        itemCount: sharedItemCount,
        groupProps: {
          expanded: teamAreasExpanded,
          fillLayout: 'single-top-fill',
          placement: 'top',
        },
        sectionProps: {
          action: renderSectionAction?.('shared'),
          expanded: teamAreasExpanded,
          headerRef: teamAreasHeaderDropTargetRef,
          onExpandedChange: expanded =>
            navigationPanelService.setCollapsed(teamAreasPath, !expanded),
          title: teamAreasTitle,
        },
      }}
    />
  );
}

export function WorkspaceAppScopedSidebarAreas<
  TItem extends WorkspaceAppScopedSidebarItem,
>({
  items,
  emptyState,
  getItemKey,
  onItemScopeChangeRequest,
  personalTitle,
  persistenceKey,
  renderItem,
  renderSectionAction,
  teamAreasTitle,
}: {
  items: readonly TItem[];
  emptyState?: (scope: TItem['scope']) => ReactNode;
  getItemKey?: (item: TItem) => string;
  onItemScopeChangeRequest?: (item: TItem, targetScope: TItem['scope']) => void;
  personalTitle: ReactNode;
  persistenceKey: string;
  renderItem: (item: TItem) => ReactNode;
  renderSectionAction?: (scope: TItem['scope']) => ReactNode;
  teamAreasTitle: ReactNode;
}): ReactElement {
  const navigationPanelService = useService(NavigationPanelService);
  const draggedItemRef = useRef<TItem | null>(null);
  const personalPath = useMemo(
    () => ['workspaceApp', persistenceKey, 'personal'],
    [persistenceKey]
  );
  const teamAreasPath = useMemo(
    () => ['workspaceApp', persistenceKey, 'teamAreas'],
    [persistenceKey]
  );
  const personalCollapsed = useLiveData(
    navigationPanelService.collapsed$(personalPath)
  );
  const teamAreasCollapsed = useLiveData(
    navigationPanelService.collapsed$(teamAreasPath)
  );
  const personalExpanded = !personalCollapsed;
  const teamAreasExpanded = !teamAreasCollapsed;
  const [personalItems, teamAreaItems] = useMemo(
    () => [
      items.filter(item => item.scope === 'private'),
      items.filter(item => item.scope === 'shared'),
    ],
    [items]
  );
  const renderScopedItem = useCallback(
    (item: TItem) => {
      const content = renderItem(item);
      if (!onItemScopeChangeRequest || !getItemKey) return content;
      return (
        <div
          className={styles.areaItem}
          key={getItemKey(item)}
          draggable
          onDragEnd={() => {
            draggedItemRef.current = null;
          }}
          onDragStart={event => {
            const nestedDraggable =
              event.target instanceof Element
                ? event.target.closest('[draggable="true"]')
                : null;
            if (nestedDraggable !== event.currentTarget) return;
            draggedItemRef.current = item;
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData(
              'application/x-streamlain-scoped-sidebar-item',
              getItemKey(item)
            );
          }}
        >
          {content}
        </div>
      );
    },
    [getItemKey, onItemScopeChangeRequest, renderItem]
  );
  const listDropProps = useCallback(
    (targetScope: TItem['scope']): ComponentProps<'div'> => ({
      onDragOver: (event: ReactDragEvent<HTMLDivElement>) => {
        const item = draggedItemRef.current;
        if (!item || item.scope === targetScope) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      },
      onDrop: (event: ReactDragEvent<HTMLDivElement>) => {
        const item = draggedItemRef.current;
        if (!item || item.scope === targetScope) return;
        event.preventDefault();
        draggedItemRef.current = null;
        onItemScopeChangeRequest?.(item, targetScope);
      },
    }),
    [onItemScopeChangeRequest]
  );

  return (
    <WorkspaceAppSidebarAreas
      persistenceKey={persistenceKey}
      personal={{
        emptyState: emptyState?.('private'),
        itemCount: personalItems.length,
        listProps: listDropProps('private'),
        groupProps: {
          expanded: personalExpanded,
          placement: 'top',
        },
        sectionProps: {
          action: renderSectionAction?.('private'),
          expanded: personalExpanded,
          onExpandedChange: expanded =>
            navigationPanelService.setCollapsed(personalPath, !expanded),
          title: personalTitle,
        },
        children: personalItems.map(renderScopedItem),
      }}
      teamAreas={{
        emptyState: emptyState?.('shared'),
        itemCount: teamAreaItems.length,
        listProps: listDropProps('shared'),
        groupProps: {
          expanded: teamAreasExpanded,
          fillLayout: 'single-top-fill',
          placement: 'top',
        },
        sectionProps: {
          action: renderSectionAction?.('shared'),
          expanded: teamAreasExpanded,
          onExpandedChange: expanded =>
            navigationPanelService.setCollapsed(teamAreasPath, !expanded),
          title: teamAreasTitle,
        },
        children: teamAreaItems.map(renderScopedItem),
      }}
    />
  );
}

type ExpandableGroupProps = Omit<
  ComponentProps<typeof SidebarExpandableGroup>,
  'children'
>;
type ExpandableSectionProps = Omit<
  ComponentProps<typeof SidebarExpandableSection>,
  'children'
>;

type WorkspaceAppSidebarAreaResizeProps = Pick<
  ExpandableSectionProps,
  | 'contentHeight'
  | 'onContentHeightChange'
  | 'onContentHeightCommit'
  | 'onResizeActiveChange'
>;

export type WorkspaceAppSidebarAreaResize =
  | { mode?: 'measured' }
  | ({ mode: 'controlled' } & WorkspaceAppSidebarAreaResizeProps);

type WorkspaceAppSidebarAreaListProps = Omit<
  ComponentProps<'div'>,
  'children' | 'ref'
> & {
  'data-drag-over'?: boolean;
};

export interface WorkspaceAppSidebarArea {
  children?: ReactNode;
  content?: ReactNode;
  defaultVisibleItems?: number;
  emptyState?: ReactNode;
  groupProps: ExpandableGroupProps;
  itemCount?: number;
  layout?: 'default' | 'inherit';
  listProps?: WorkspaceAppSidebarAreaListProps;
  listRef?: ComponentProps<'div'>['ref'];
  measurementRef?: RefObject<HTMLDivElement | null>;
  minimumVisibleItems?: number;
  resize?: WorkspaceAppSidebarAreaResize;
  snapTargetSelector?: string;
  sectionProps: ExpandableSectionProps;
}

export function WorkspaceAppSidebarArea({
  children,
  content,
  emptyState,
  groupProps,
  itemCount,
  layout = 'default',
  listProps,
  listRef,
  sectionProps,
}: WorkspaceAppSidebarArea): ReactElement {
  return (
    <SidebarExpandableGroup
      {...groupProps}
      className={clsx(
        layout === 'default' && styles.areaGroup,
        styles.collapsedHeaderOnlyGroup,
        groupProps.className
      )}
    >
      <SidebarExpandableSection
        contentPadding="none"
        placement="top"
        {...sectionProps}
        className={clsx(
          layout === 'default' && styles.areaSection,
          sectionProps.className
        )}
        contentClassName={clsx(
          layout === 'default' && styles.areaContent,
          sectionProps.contentClassName
        )}
      >
        {content !== undefined ? (
          content
        ) : (
          <WorkspaceAppSidebarAreaList ref={listRef} {...listProps}>
            {itemCount === 0 && emptyState !== undefined ? (
              <div
                className={styles.areaEmptyState}
                data-sidebar-section-snap-target="true"
              >
                {emptyState}
              </div>
            ) : (
              children
            )}
          </WorkspaceAppSidebarAreaList>
        )}
      </SidebarExpandableSection>
    </SidebarExpandableGroup>
  );
}

export function WorkspaceAppSidebarAreas({
  personal,
  persistenceKey,
  teamAreas,
}: {
  personal: WorkspaceAppSidebarArea;
  persistenceKey: string;
  teamAreas: WorkspaceAppSidebarArea;
}): ReactElement {
  const navigationPanelService = useService(NavigationPanelService);
  const personalHeightPath = useMemo(
    () => ['workspaceApp', persistenceKey, 'personal', 'height'],
    [persistenceKey]
  );
  const preferredPersonalHeight = useLiveData(
    navigationPanelService.sidebarAreaHeight$(personalHeightPath)
  );
  const commitPreferredPersonalHeight = useCallback(
    (height: number) =>
      navigationPanelService.setSidebarAreaHeight(personalHeightPath, height),
    [navigationPanelService, personalHeightPath]
  );
  const personalListRef = useRef<HTMLDivElement | null>(null);
  const personalMeasurementRef = personal.measurementRef ?? personalListRef;
  const personalResize = useWorkspaceAppSidebarAreaResize({
    defaultVisibleItems: personal.defaultVisibleItems,
    expanded: personal.sectionProps.expanded,
    itemCount: personal.itemCount ?? 0,
    listRef: personalMeasurementRef,
    minimumVisibleItems: personal.minimumVisibleItems,
    onPreferredContentHeightCommit: commitPreferredPersonalHeight,
    preferredContentHeight: preferredPersonalHeight,
    snapTargetSelector: personal.snapTargetSelector,
  });
  const controlledPersonalResize =
    personal.resize?.mode === 'controlled'
      ? {
          contentHeight: personal.resize.contentHeight,
          onContentHeightChange: personal.resize.onContentHeightChange,
          onContentHeightCommit: personal.resize.onContentHeightCommit,
          onResizeActiveChange: personal.resize.onResizeActiveChange,
        }
      : personalResize;

  return (
    <>
      <WorkspaceAppSidebarArea
        {...personal}
        groupProps={{
          ...personal.groupProps,
          className: clsx(
            styles.firstAreaGroup,
            styles.resizableAreaGroup,
            personal.groupProps.className
          ),
        }}
        listRef={personal.listRef ?? personalMeasurementRef}
        sectionProps={{
          ...personal.sectionProps,
          ...controlledPersonalResize,
          resizable: personal.sectionProps.resizable ?? true,
          resizeHitArea:
            personal.sectionProps.resizeHitArea ?? 'inside-and-after',
        }}
      />
      <WorkspaceAppSidebarArea
        {...teamAreas}
        groupProps={{
          ...teamAreas.groupProps,
          className: clsx(
            styles.terminalAreaGroup,
            teamAreas.groupProps.className
          ),
        }}
      />
    </>
  );
}

export const WorkspaceAppSidebarAreaList = forwardRef<
  HTMLDivElement,
  ComponentProps<'div'>
>(({ children, className, ...props }, ref) => (
  <div
    {...props}
    ref={ref}
    className={clsx(styles.areaList, className)}
    data-sidebar-scroll-viewport="true"
    data-workspace-app-sidebar-area-list="true"
  >
    {children}
  </div>
));

WorkspaceAppSidebarAreaList.displayName = 'WorkspaceAppSidebarAreaList';
